import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageSquare, User as UserIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { nameInitials } from "../../lib/nameInitials";
import { useAuthStore } from "../../store/authStore";

/**
 * Small violet "X tarafından atandı" badge used on planned activities.
 * Clicking it opens a popover exposing two quick actions on the person
 * who delegated the task:
 *   - "Mesaj Gönder" — dispatches the global `dm-open` event which the
 *     floating DmWidget listens to (see `DmWidget.jsx`).
 *   - "Profili Görüntüle" — navigates to `/app/users?highlight=<id>`
 *     which scrolls + glows that row in the Users page.
 * If the delegator is the current user we still render the label but
 * make it non-interactive since messaging yourself is pointless.
 */
export default function AssignedByBadge({
  userId,
  userName,
  userEmail,
  size = "md",
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id;
  // Only super_admin can access /app/users, so the "Profili Görüntüle"
  // action is scoped to them. Regular users still get the mini profile
  // card + "Mesaj Gönder", which is the primary action anyway.
  const canViewProfile = ["super_admin", "ultra_admin"].includes(currentUser?.role);

  if (!userId || !userName) return null;
  const isSelf = userId === currentUserId;

  // Two tiers of visual density: `sm` for the dashboard widget where
  // rows are tightly packed, `md` for the full-page list.
  const sizes = size === "sm"
    ? { pill: "px-1.5 py-0.5", initials: "h-3 w-3 text-[7px]", text: "text-[11px]" }
    : { pill: "px-1.5 py-0.5", initials: "h-3.5 w-3.5 text-[8px]", text: "text-[11px]" };

  const badge = (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-violet-50 font-medium text-violet-800 ${sizes.pill} ${sizes.text}`}
    >
      <span
        className={`inline-flex items-center justify-center rounded-full bg-violet-600 font-semibold text-white ${sizes.initials}`}
      >
        {nameInitials(userName)}
      </span>
      {userName} tarafından atandı
    </span>
  );

  if (isSelf) {
    // Delegated to myself — no popover, just render the label.
    return badge;
  }

  const sendMessage = () => {
    setOpen(false);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("dm-open", {
          detail: {
            kind: "dm",
            id: userId,
            name: userName,
            email: userEmail || "",
          },
        })
      );
    }, 30);
  };

  const viewProfile = () => {
    setOpen(false);
    navigate(`/app/users?highlight=${encodeURIComponent(userId)}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`assigned-by-badge-${userId}`}
          onClick={(e) => {
            // Stop click from bubbling to the parent <Link> so opening the
            // popover doesn't also navigate to the construction detail.
            // We deliberately DO NOT call preventDefault(): Radix
            // Popover's asChild trigger skips its own toggle if
            // `event.defaultPrevented` is true, which would leave the
            // popover closed. stopPropagation alone stops the outer
            // navigation without breaking the trigger.
            e.stopPropagation();
          }}
          className="rounded-full outline-none transition-transform hover:scale-105 hover:ring-2 hover:ring-violet-200 focus-visible:ring-2 focus-visible:ring-violet-400"
          aria-label={`${userName} — mesaj gönder`}
        >
          {badge}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-64 p-0"
        data-testid={`assigned-by-popover-${userId}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mini profile header */}
        <div className="flex items-center gap-2.5 border-b border-zinc-100 bg-gradient-to-br from-violet-50 to-white px-3 py-2.5">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-xs font-bold text-white ring-2 ring-white">
            {nameInitials(userName)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-zinc-900">
              {userName}
            </div>
            {userEmail && (
              <div className="truncate text-[11px] text-zinc-500">
                {userEmail}
              </div>
            )}
          </div>
        </div>
        {/* Actions */}
        <div className="flex flex-col p-1">
          <button
            type="button"
            onClick={sendMessage}
            data-testid={`assigned-by-send-message-${userId}`}
            className="flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-zinc-800 transition-colors hover:bg-violet-50 hover:text-violet-800"
          >
            <MessageSquare className="h-4 w-4 text-violet-600" />
            Mesaj Gönder
          </button>
          {canViewProfile && (
            <button
              type="button"
              onClick={viewProfile}
              data-testid={`assigned-by-view-profile-${userId}`}
              className="flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm text-zinc-800 transition-colors hover:bg-zinc-100"
            >
              <UserIcon className="h-4 w-4 text-zinc-600" />
              Profili Görüntüle
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
